import { NormalButton } from '@/shared/ui/components';
import { DOUBLE_HYPHEN_BULLET } from '@/shared/config';
import { generateFullAddress, dateToString } from '@/shared/lib';
import { Divider, Timeline } from 'antd';
import classNames from 'classnames';
import _ from 'lodash';
import Skeleton from 'react-loading-skeleton';
import { t } from 'ttag';
import { useDrawerContent } from './useDrawerContent';
import type { DrawerContentProps } from './drawerContent.types';

const DrawerContent = ({
  study,
  biofluxDirect,
  isRecipientInformation,
  isDrawer,
  isStudyDetails,
  handleRefreshTracking,
}: DrawerContentProps) => {
  const { state, setState } = useDrawerContent();

  if (_.isEmpty(study) || _.isEmpty(biofluxDirect)) {
    return null;
  }

  const shippingInformation = isRecipientInformation
    ? biofluxDirect?.recipientInformation
    : biofluxDirect?.senderInformation;

  const mainHeader = isRecipientInformation ? t`Sending Device Status` : t`Returning Device Status`;

  if (_.isEmpty(shippingInformation?.latestLabel)) {
    return (
      <div className="biodirect-status-container">
        <div className="main-header">
          <span>{mainHeader}</span>
        </div>
        {isStudyDetails && (
          <Divider
            aria-hidden="true"
            style={{
              margin: 0,
              marginBlock: 16,
            }}
          />
        )}
        <div
          className={classNames('no-shipping-label', {
            '--drawer': isDrawer,
          })}
        >
          <span className="no-shipping-label__text">{t`No shipping label created yet`}</span>
        </div>
      </div>
    );
  }

  const { latestLabel } = shippingInformation;
  const { shipmentDate, transaction, rate, tracking } = latestLabel;
  const { trackingNumber, trackingUrlProvider, eta } = transaction;
  const { carrier, carrierLogo200 } = rate;
  const { trackingHistory } = tracking;

  const orderedTrackingHistory = _.orderBy(trackingHistory, ['statusDate'], ['desc']);
  /**
   * Holds the timeline entries rendered for a shipment's tracking history. Maps the ordered history into timeline items, each showing the status (with PRE_TRANSIT spelled out), the status date formatted in the study's timezone with a placeholder fallback, the full location address and the status details.
   *
   * @returns Timeline item descriptors for the tracking history.
   */
  const items = orderedTrackingHistory?.map((item) => ({
    children: (
      <div className="label-timeline-item-content">
        <div className="status">
          {item.status === 'PRE_TRANSIT' ? 'Pre-transit' : _.capitalize(item.status)}
        </div>
        <div className="date">
          {dateToString({
            startStudyDate: study.start,
            isoDate: item.statusDate,
            timezone: study.timezone,
            studyTimeOffset: study.studyTimeOffset,
            facilityCountry: study.facility?.contact?.country,
            facilityTimezone: study.facility?.timezone,
            format: 'MM/DD/YYYY',
          }) ?? DOUBLE_HYPHEN_BULLET}
        </div>
        <div className="address">
          {generateFullAddress({
            contact: {
              ...item.location,
              address: item.location?.street1,
            },
            includedCountry: true,
          })}
        </div>
        <div className="status-details">{item.statusDetails}</div>
      </div>
    ),
  }));

  return (
    <div className="biodirect-status-container">
      <div className="main-header">
        <span>{mainHeader}</span>
      </div>
      {isStudyDetails && (
        <Divider
          aria-hidden="true"
          style={{
            margin: 0,
            marginBlock: 16,
          }}
        />
      )}
      <div
        className={classNames('main-content', {
          '--drawer': isDrawer,
        })}
      >
        <div className="main-content-item">
          <span className="title">{`${t`Tracking number`}:`}</span>
          <a
            className="tracking-number"
            href={trackingUrlProvider}
            target="_blank"
            rel="noreferrer"
          >
            {trackingNumber || '--'}
          </a>
        </div>
        <div className="main-content-item">
          <span className="title">{`${t`Shipped date`}:`}</span>
          <span className="content">
            {dateToString({
              startStudyDate: study.start,
              isoDate: shipmentDate,
              timezone: study.timezone,
              studyTimeOffset: study.studyTimeOffset,
              facilityCountry: study.facility?.contact?.country,
              facilityTimezone: study.facility?.timezone,
              format: 'MM/DD/YYYY',
            }) ?? DOUBLE_HYPHEN_BULLET}
          </span>
        </div>
        <div className="main-content-item">
          <span className="title">{`${t`Estimated arrival`}:`}</span>
          <span className="content">
            {dateToString({
              startStudyDate: study.start,
              isoDate: eta,
              timezone: study.timezone,
              studyTimeOffset: study.studyTimeOffset,
              facilityCountry: study.facility?.contact?.country,
              facilityTimezone: study.facility?.timezone,
              format: 'MM/DD/YYYY',
            }) ?? DOUBLE_HYPHEN_BULLET}
          </span>
        </div>
        <div className="main-content-item">
          <span className="title">{`${t`Carrier`}:`}</span>
          <div className="carrier-container">
            <img className="carrier-logo" src={carrierLogo200} alt="Carrier logo" />
            <span className="carrier-name">{carrier}</span>
          </div>
        </div>
        <div className="timeline-container">
          <div className="timeline-container__title-container">
            <span className="title">{t`Timeline`}</span>
            <NormalButton
              id="refresh-btn"
              isText
              isBold
              buttonName={t`Refresh`}
              onClick={() => {
                setState({ isRefreshingTracking: true });
                handleRefreshTracking({
                  type: isRecipientInformation ? 'InboundBiodirect' : 'OutboundBiodirect',
                  transactionId: transaction?.id,
                  ticketId: biofluxDirect.id,
                  callback: () => {
                    setState({ isRefreshingTracking: false });
                  },
                });
              }}
            />
          </div>
          <div className="label-timeline-box">
            {state.isRefreshingTracking ? (
              <Skeleton
                style={{
                  width: '100%',
                  height: '100%',
                }}
              />
            ) : (
              orderedTrackingHistory?.length > 0 && (
                <Timeline items={items} aria-label={t`Tracking history`} />
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DrawerContent;
